import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { ImpactResult, RiskStatus } from '../types';
import { getRiskColors, getRiskLabel } from '../utils/riskUtils';
import { formatDate } from '../utils/dateUtils';
import { clearDemoSessionToken } from '../utils/demoSession';
import { isAdminEmail } from '../utils/adminAllowlist';
import { Logo, BRAND_NAME } from './Logo';

/* ─── Icons (inline SVG to avoid extra deps) ─── */

export const IconChevronLeft: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

export const IconSun: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="5" />
    <path strokeLinecap="round" d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

export const IconMoon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
  </svg>
);

/* ─── Navigation ─── */

interface NavTab {
  label: string;
  path: (projectId: string) => string;
}

// Project-level screens, in header order.
const PROJECT_NAV: NavTab[] = [
  { label: 'Dashboard', path: (id) => `/project/${id}/dashboard` },
  { label: 'Lookahead', path: (id) => `/project/${id}/lookahead` },
  { label: 'Ask', path: (id) => `/project/${id}/ask` },
  { label: 'Reports', path: (id) => `/project/${id}/reports` },
  { label: 'Upload', path: (id) => `/project/${id}/upload` },
];

const IconMenu: React.FC<{ className?: string }> = ({ className = 'w-6 h-6' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

const IconClose: React.FC<{ className?: string }> = ({ className = 'w-6 h-6' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/* ─── AppHeader — one header for every authenticated screen ───
   Background spans the full width; the content row sits in the shared .container, so the
   logo's left edge and the user menu's right edge line up with every page's content.
   ≥1280px (xl):  logo + all links + Admin pill + name/org + avatar, one row.
   768–1279 (md): primary links (Portfolio, Dashboard, Lookahead, Ask) + "More" (Reports,
                  Upload, Admin); avatar only; tighter link gaps so the row fits.
   <768px:        logo + hamburger → full-height drawer (all links, name/org, avatar,
                  Sign out / Exit demo).
   Its rendered height is published as --app-header-h for sticky offsets below it. */

const HEADER_H = 'clamp(60px, 5vw, 76px)';
const LINK_SIZE = 'text-[15px]';

interface AppHeaderProps {
  searchValue?: string;
  onSearch?: (q: string) => void;
}

export const AppHeader: React.FC<AppHeaderProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject, user, userMode } = useApp();
  const { userName, userEmail, logout } = useAuth();
  const isDemoMode = userMode === 'demo';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  const isInsideProject = location.pathname.startsWith('/project/');
  const projectId = isInsideProject ? location.pathname.split('/')[2] : null;
  // Viewer role on this project — Upload is hidden rather than shown and then 403'd.
  const projectTabs = PROJECT_NAV.filter((t) => !(t.label === 'Upload' && selectedProject?.role_on_project === 'Viewer'));
  const activeLabel = (() => {
    const p = location.pathname;
    if (p.includes('/dashboard')) return 'Dashboard';
    if (p.includes('/lookahead')) return 'Lookahead';
    if (p.includes('/ask')) return 'Ask';
    if (p.includes('/reports')) return 'Reports';
    if (p.includes('/upload')) return 'Upload';
    if (p.startsWith('/admin')) return 'Admin';
    if (p.startsWith('/account')) return 'Account';
    return 'Portfolio';
  })();

  const displayName = userName || user?.name || '';
  const company = user?.company ?? '';
  const initials = displayName
    ? displayName.trim().split(/\s+/).filter(Boolean).map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';
  const isAdmin = userMode === 'authenticated' && isAdminEmail(userEmail);

  // Close menus on navigation; Escape / outside tap closes them too.
  useEffect(() => { setDrawerOpen(false); setMoreOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!drawerOpen && !moreOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setDrawerOpen(false); setMoreOpen(false); } };
    const onDown = (e: PointerEvent) => {
      if (moreOpen && moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown); };
  }, [drawerOpen, moreOpen]);
  // Drawer open → page behind it doesn't scroll.
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);
  // Publish the header's real height (it varies with the demo banner and the fluid bar).
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--app-header-h', `${el.getBoundingClientRect().height}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const exitDemo = () => {
    clearDemoSessionToken();
    window.location.href = '/';
  };
  // Distinct from a plain exit — lands on Landing with the Request Access form open.
  const requestAccessFromDemo = () => {
    clearDemoSessionToken();
    window.location.href = '/?requestAccess=1';
  };

  // Inline nav link (md+). `hideBelowXl` = moves into "More" on tablets.
  const navLink = (label: string, onClick: () => void, opts: { active?: boolean; hideBelowXl?: boolean; text?: string } = {}) => (
    <button
      key={label}
      onClick={onClick}
      className={`relative ${opts.hideBelowXl ? 'hidden xl:flex' : 'flex'} items-center h-full ${LINK_SIZE} whitespace-nowrap transition-colors ${
        opts.active ? 'text-white font-semibold' : 'text-white/[0.72] font-medium hover:text-white'
      }`}
      aria-current={opts.active ? 'page' : undefined}
    >
      {opts.text ?? label}
      {opts.active && <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-on-dark" />}
    </button>
  );

  // "More" (tablet) holds the links that don't fit inline, plus Admin.
  const moreItems: { label: string; onClick: () => void }[] = [
    ...(isInsideProject && projectId
      ? projectTabs.filter((t) => t.label === 'Reports' || t.label === 'Upload').map((t) => ({ label: t.label, onClick: () => navigate(t.path(projectId)) }))
      : []),
    ...(isAdmin ? [{ label: 'Admin', onClick: () => navigate('/admin') }] : []),
  ];
  const moreActive = moreItems.some((i) => i.label === activeLabel);

  const drawerRow = (label: string, onClick: () => void, opts: { active?: boolean; indent?: boolean; muted?: boolean } = {}) => (
    <button
      key={label}
      onClick={onClick}
      className={`w-full text-left min-h-[48px] px-5 flex items-center text-[15px] border-t border-white/10 ${
        opts.indent ? 'pl-9' : ''
      } ${opts.active ? 'bg-white/10 font-semibold text-white' : opts.muted ? 'text-white/70' : 'text-white/90'} active:bg-white/15`}
    >
      {label}
    </button>
  );

  return (
    <header ref={headerRef} className="sticky top-0 z-40 bg-ink text-white shadow-md safe-top">
      {isDemoMode && (
        <div className="bg-accent">
          <div className="container flex items-center justify-center gap-x-3 gap-y-1 flex-wrap py-1.5 text-xs">
            <span className="font-semibold">Demo Mode</span>
            <span className="hidden sm:inline text-white/75">You're viewing a sample project — nothing here is saved.</span>
            <button
              onClick={requestAccessFromDemo}
              className="bg-white/15 rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap tap"
            >
              <span className="sm:hidden">Request access →</span>
              <span className="hidden sm:inline">Ready to use your own schedule? Request access →</span>
            </button>
          </div>
        </div>
      )}

      <div className="container flex items-center" style={{ height: HEADER_H }}>
        {/* Brand — in demo mode this is the way back to Landing, since there's no Sign Out */}
        <button
          onClick={() => (isDemoMode ? exitDemo() : navigate('/portfolio'))}
          className="flex items-center shrink-0 md:mr-6 xl:mr-[54px] hover:opacity-80 transition-opacity tap"
          aria-label={isDemoMode ? 'Exit demo' : `${BRAND_NAME} — Portfolio`}
        >
          <Logo variant="onDark" wordmarkSize="clamp(24px, 2.4vw, 34px)" />
        </button>

        {/* md+: inline links (Reports/Upload collapse into More below xl) */}
        <nav className="hidden md:flex items-stretch self-stretch gap-5 xl:gap-9 min-w-0" aria-label="Main">
          {isInsideProject && projectId ? (
            <>
              {navLink('Portfolio', () => navigate('/portfolio'), { text: '← Portfolio' })}
              {projectTabs.map((tab) =>
                navLink(tab.label, () => navigate(tab.path(projectId)), {
                  active: activeLabel === tab.label,
                  hideBelowXl: tab.label === 'Reports' || tab.label === 'Upload',
                })
              )}
            </>
          ) : (
            navLink('Portfolio', () => navigate('/portfolio'), { active: activeLabel === 'Portfolio' })
          )}

          {moreItems.length > 0 && (
            <div ref={moreRef} className="relative flex xl:hidden items-stretch">
              <button
                onClick={() => setMoreOpen((o) => !o)}
                className={`relative flex items-center gap-1 h-full ${LINK_SIZE} whitespace-nowrap ${
                  moreActive ? 'text-white font-semibold' : 'text-white/[0.72] font-medium hover:text-white'
                }`}
                aria-haspopup="menu"
                aria-expanded={moreOpen}
              >
                More
                <svg className={`w-4 h-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                {moreActive && <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-on-dark" />}
              </button>
              {moreOpen && (
                <div role="menu" className="absolute top-full left-0 mt-1 min-w-[180px] bg-ink border border-white/10 rounded-[8px] shadow-xl py-1">
                  {moreItems.map((i) => (
                    <button
                      key={i.label}
                      role="menuitem"
                      onClick={i.onClick}
                      className={`w-full text-left px-4 min-h-[44px] text-[15px] ${i.label === activeLabel ? 'text-white font-semibold bg-white/10' : 'text-white/85 hover:bg-white/10'}`}
                    >
                      {i.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* md+: identity (full at xl, avatar only below) / exit demo. Conditionally
            rendered, not CSS-hidden: a demo session has no real identity in the DOM at all. */}
        <div className="hidden md:flex items-center gap-3.5 ml-auto pl-4 min-w-0">
          {userMode === 'authenticated' ? (
            <>
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="hidden xl:inline-flex bg-white/10 rounded-full px-4 py-1.5 text-[16px] font-semibold whitespace-nowrap"
                >
                  Admin
                </button>
              )}
              <div className="hidden xl:block text-right min-w-0 max-w-[160px] 2xl:max-w-[220px]">
                <div className="text-[16px] font-semibold leading-tight truncate" title={displayName}>{displayName}</div>
                <div className="text-[14px] text-white/70 leading-tight truncate" title={company}>{company}</div>
              </div>
              <button
                onClick={() => navigate('/account')}
                className="w-11 h-11 rounded-full bg-accent text-white text-[15px] font-bold flex items-center justify-center shrink-0"
                aria-label={`Account — ${displayName}`}
                title={displayName}
              >
                {initials}
              </button>
            </>
          ) : (
            <button onClick={exitDemo} className="bg-white/10 rounded-full px-4 py-1.5 text-[15px] xl:text-[16px] font-semibold whitespace-nowrap tap">
              Exit demo
            </button>
          )}
        </div>

        {/* <md: hamburger */}
        <button
          onClick={() => setDrawerOpen(true)}
          className="md:hidden ml-auto -mr-1 w-11 h-11 flex items-center justify-center rounded-lg active:bg-white/10"
          aria-label="Open menu"
          aria-expanded={drawerOpen}
        >
          <IconMenu />
        </button>
      </div>

      {/* <md: full-height drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <nav className="absolute top-0 right-0 bottom-0 w-[min(320px,86vw)] bg-ink shadow-2xl flex flex-col overflow-y-auto safe-top safe-bottom">
            <div className="flex items-center justify-between px-5 min-h-[60px]">
              <span className="text-sm font-semibold text-white/70">Menu</span>
              <button
                onClick={() => setDrawerOpen(false)}
                className="-mr-2 w-11 h-11 flex items-center justify-center rounded-lg active:bg-white/10"
                aria-label="Close menu"
              >
                <IconClose />
              </button>
            </div>
            {userMode === 'authenticated' && (
              <div className="flex items-center gap-3 px-5 py-4 border-t border-white/10">
                <div className="w-11 h-11 rounded-full bg-accent text-white text-[15px] font-bold flex items-center justify-center shrink-0">{initials}</div>
                <div className="min-w-0">
                  <div className="text-[16px] font-semibold truncate" title={displayName}>{displayName}</div>
                  <div className="text-[14px] text-white/70 truncate" title={company}>{company}</div>
                </div>
              </div>
            )}
            {drawerRow('Portfolio', () => navigate('/portfolio'), { active: activeLabel === 'Portfolio' })}
            {isInsideProject && projectId && projectTabs.map((tab) =>
              drawerRow(tab.label, () => navigate(tab.path(projectId)), { active: activeLabel === tab.label, indent: true })
            )}
            {isAdmin && drawerRow('Admin', () => navigate('/admin'), { active: activeLabel === 'Admin' })}
            {userMode === 'authenticated' && drawerRow('Account', () => navigate('/account'), { active: activeLabel === 'Account' })}
            <div className="mt-auto border-t border-white/10">
              {userMode === 'authenticated'
                ? drawerRow('Sign out', logout, { muted: true })
                : drawerRow('Exit demo', exitDemo, { muted: true })}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

/* ─── StatusHero ─── */

interface StatusHeroProps {
  riskStatus: RiskStatus | string;
  title: string;
  description: string;
  stats?: Array<{ label: string; value: string }>;
}

export const StatusHero: React.FC<StatusHeroProps> = ({ riskStatus, title, description, stats }) => {
  const colors = getRiskColors(riskStatus);
  const label = getRiskLabel(riskStatus);

  return (
    <div
      className={`mx-4 mt-4 rounded-[8px] p-4 border ${colors.bgClass} ${colors.borderClass} border-l-4`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${colors.textClass}`}>
            {title}
          </p>
          <p className={`text-sm leading-snug ${colors.textClass}`}>{description}</p>
        </div>
        <span
          className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full border ${colors.bgClass} ${colors.textClass} ${colors.borderClass}`}
        >
          {label}
        </span>
      </div>
      {stats && stats.length > 0 && (
        <div className="flex gap-4 mt-3 pt-3 border-t border-current/10">
          {stats.map((s) => (
            <div key={s.label}>
              <p className={`text-xs opacity-70 ${colors.textClass}`}>{s.label}</p>
              <p className={`text-sm font-semibold ${colors.textClass}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── ImpactAnalysis ─── */

interface ImpactAnalysisProps {
  result: ImpactResult;
  activityNames?: Record<string, string>;
}

export const ImpactAnalysis: React.FC<ImpactAnalysisProps> = ({ result, activityNames = {} }) => {
  const colors = getRiskColors(result.risk_level);
  const resolveName = (id: string) => activityNames[id] || id;

  return (
    <div className={`rounded-[8px] p-4 border ${colors.bgClass} ${colors.borderClass} fade-in`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-bold uppercase tracking-wide ${colors.textClass}`}>
          Impact Analysis
        </span>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${colors.bgClass} ${colors.textClass} ${colors.borderClass}`}>
          {result.risk_level} Risk
        </span>
      </div>

      <p className={`text-sm mb-3 leading-relaxed ${colors.textClass}`}>{result.summary}</p>

      {result.forecast_before_early_finish && result.activity_early_finish && (
        <div className="mb-3 p-2.5 rounded-[6px] bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
          <p className="text-xs text-blue-700 dark:text-blue-300 font-semibold mb-0.5">Date is ahead of schedule</p>
          <p className="text-xs text-blue-600 dark:text-blue-400">
            The CPM network predicts this activity's earliest possible finish is <strong>{formatDate(result.activity_early_finish)}</strong> (based on predecessor delays).
            Your date is before that — no additional project impact.
            To see cascade impact, enter a date after <strong>{formatDate(result.activity_early_finish)}</strong>.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className={`text-xs opacity-70 ${colors.textClass}`}>Days added</p>
          <p className={`text-sm font-semibold ${colors.textClass}`}>
            {result.days_added_to_completion > 0
              ? `+${result.days_added_to_completion}d`
              : result.days_added_to_completion === 0
              ? 'None'
              : `${result.days_added_to_completion}d`}
          </p>
        </div>
        <div>
          <p className={`text-xs opacity-70 ${colors.textClass}`}>Completion moves</p>
          <p className={`text-sm font-semibold ${colors.textClass}`}>
            {result.completion_date_moves ? 'Yes' : 'No'}
          </p>
        </div>
      </div>

      {result.new_completion_date && (
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <p className={`text-xs opacity-70 ${colors.textClass}`}>Original finish</p>
            <p className={`text-sm font-semibold ${colors.textClass}`}>{result.original_completion_date ? formatDate(result.original_completion_date) : '—'}</p>
          </div>
          <div>
            <p className={`text-xs opacity-70 ${colors.textClass}`}>New finish</p>
            <p className={`text-sm font-semibold ${colors.textClass}`}>{formatDate(result.new_completion_date)}</p>
          </div>
        </div>
      )}

      {(() => {
        const allAffected = [
          ...(result.directly_affected ?? []),
          ...(result.indirectly_affected ?? []),
        ];
        if (allAffected.length === 0) return null;
        const cap = 8;
        return (
          <div className="mb-2">
            <p className={`text-xs font-semibold mb-1 ${colors.textClass}`}>
              Affected activities ({allAffected.length})
            </p>
            <div className="flex flex-wrap gap-1">
              {allAffected.slice(0, cap).map((id) => (
                <span key={id} className={`text-xs px-2 py-0.5 rounded-md border ${colors.borderClass} ${colors.textClass}`}>
                  {resolveName(id)}
                </span>
              ))}
              {allAffected.length > cap && (
                <span className={`text-xs ${colors.textClass} opacity-70`}>
                  +{allAffected.length - cap} more
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {result.trade_conflicts && result.trade_conflicts !== 'None identified' && (
        <div className="mb-2">
          <p className={`text-xs font-semibold mb-1 ${colors.textClass}`}>Trade conflicts</p>
          <p className={`text-xs leading-relaxed ${colors.textClass}`}>{result.trade_conflicts}</p>
        </div>
      )}

      {result.milestones_at_risk?.length > 0 && (
        <div className="mb-2">
          <p className={`text-xs font-semibold mb-1 ${colors.textClass}`}>Milestones at risk</p>
          <ul className={`text-xs space-y-0.5 ${colors.textClass}`}>
            {result.milestones_at_risk.map((m) => (
              <li key={m} className="flex gap-1">
                <span>•</span>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.recovery_option && (
        <div className={`mt-3 pt-3 border-t border-current/10`}>
          <p className={`text-xs font-semibold mb-1 ${colors.textClass}`}>Recovery option</p>
          <p className={`text-xs leading-relaxed ${colors.textClass}`}>{result.recovery_option}</p>
        </div>
      )}
    </div>
  );
};

/* ─── SkeletonCard ─── */

/* ─── Page shell ───
   The one wrapper every signed-in page uses: app header, the page background
   (ground token) and the shared .container. `className` adds page-specific spacing. */
export const PageShell: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className="min-h-dvh bg-ground">
    <AppHeader />
    <main className={`container safe-bottom ${className ?? ''}`}>
      {children}
    </main>
  </div>
);

export const SkeletonCard: React.FC = () => (
  <div className="bg-white dark:bg-gray-800 rounded-[8px] p-4 shadow-sm border border-gray-100 dark:border-gray-700">
    <div className="flex justify-between mb-3">
      <div className="skeleton h-4 w-2/3 rounded" />
      <div className="skeleton h-5 w-16 rounded-full" />
    </div>
    <div className="skeleton h-3 w-1/3 rounded mb-3" />
    <div className="skeleton h-1.5 w-full rounded-full" />
  </div>
);

/* ─── LoadingSpinner ─── */

interface LoadingSpinnerProps {
  message?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-12 gap-3">
    <div className="w-10 h-10 border-3 border-accent border-t-transparent rounded-full animate-spin border-[3px]" />
    {message && <p className="text-sm text-muted dark:text-gray-400 text-center max-w-xs">{message}</p>}
  </div>
);

/* ─── ErrorMessage ─── */

interface ErrorMessageProps {
  message: string;
  onRetry?: () => void;
}

export const ErrorMessage: React.FC<ErrorMessageProps> = ({ message, onRetry }) => (
  <div className="mx-4 mt-4 bg-crit-bg border border-crit-text rounded-[8px] p-4">
    <p className="text-sm text-crit-text font-medium mb-1">Something went wrong</p>
    <p className="text-xs text-crit-text mb-3">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="text-xs font-semibold text-crit-text border border-crit-text px-3 py-1.5 rounded-[8px] hover:bg-crit-text hover:text-white transition-colors min-h-[36px]"
      >
        Try again
      </button>
    )}
  </div>
);
